import { parse, isValid, addYears } from 'date-fns';
import { enUS } from 'date-fns/locale';

import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import { getPluginState } from '../../plugin-factory';

const LOCALES: Record<string, Locale> = {
	enUS,
};

const DATE_FORMATS: Record<string, string[]> = {
	'en-US': ['d/M/yyyy', 'd-M-yyyy', 'd.M.yyyy'],
};

export const SUGGESTION_TOOLBAR_ANCHOR_CLASS = 'inline-suggestion-floating-toolbar-dom-anchor';

/**
 * Retrieves the current locale from the plugin state or falls back to 'en-US'.
 */
export const getSelectedLocale = (state: EditorState): string => {
	const inlineSuggestionState = getPluginState(state);
	return inlineSuggestionState?.locale || 'en-US';
};

const MIN_DATE_LENGTH = 3;
const MAX_DATE_LENGTH = 10;

/**
 * Checks if the text contains digits and falls within typical date length constraints.
 */
const isLikelyDateString = (text: string): boolean => {
	return /\d/u.test(text) && text.length >= MIN_DATE_LENGTH && text.length <= MAX_DATE_LENGTH;
};

/**
 * Corrects a parsed date where possible by adding 2000.
 * Example: 9 → 2009, 23 → 2023
 */
const correctTwoDigitYear = (date: Date): Date => {
	const yearString = date.getFullYear().toString();
	if (yearString.length < 4) {
		return addYears(date, 2000);
	}
	return date;
};

/**
 * Parses user input as a date (using locale-based formats).
 * Returns a valid Date or null if it cannot parse.
 */
export const tryParseDateInput = (text: string, locale: string): Date | null => {
	if (!isLikelyDateString(text)) {
		return null;
	}

	const dateFnsLocale = LOCALES[locale] || enUS;
	const supportedFormats = DATE_FORMATS[locale] || [];

	for (const format of supportedFormats) {
		const parsed = parse(text, format, new Date(), { locale: dateFnsLocale });
		if (isValid(parsed)) {
			return correctTwoDigitYear(parsed);
		}
	}

	return null;
};

/**
 * Determines if the character is whitespace or punctuation that typically separates words.
 */
const isBoundaryCharacter = (char: string): boolean => {
	return /\s|[,!?;:()]/u.test(char);
};

/**
 * Extracts the word (and positions) around a collapsed selection.
 * Returns { word, from, to } or null if not in text.
 */
export const extractWordAtCursor = (
	state: EditorState,
): { word: string; from: number; to: number } | null => {
	const { selection } = state;
	if (!selection.empty) {
		return null;
	}

	const $pos = selection.$head;
	const { parent } = $pos;

	let runningOffset = 0;
	let targetTextNode = null;
	let indexInTextNode = 0;

	for (let i = 0; i < parent.childCount; i++) {
		const child = parent.child(i);
		const size = child.nodeSize;

		if (runningOffset + size >= $pos.parentOffset) {
			if (child.isText) {
				targetTextNode = child;
				indexInTextNode = $pos.parentOffset - runningOffset;
			}
			break;
		}
		runningOffset += size;
	}

	if (!targetTextNode || !targetTextNode.isText || !targetTextNode.text) {
		return null;
	}

	const fullText = targetTextNode.text;
	const posInText = Math.max(0, Math.min(indexInTextNode, fullText.length));

	let start = posInText;
	while (start > 0 && !isBoundaryCharacter(fullText[start - 1])) {
		start--;
	}

	let end = posInText;
	while (end < fullText.length && !isBoundaryCharacter(fullText[end])) {
		end++;
	}

	const word = fullText.slice(start, end);
	const rangeStart = $pos.pos - (posInText - start);
	const rangeEnd = $pos.pos + (end - posInText);

	return { word, from: rangeStart, to: rangeEnd };
};

/**
 * Returns a UTC timestamp corresponding to midnight (00:00) in UTC for the given date.
 */
export const getDateOnlyUtcTimestamp = (date: Date): number => {
	return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};
